/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { StateCreator, StoreMutatorIdentifier } from 'zustand';
import { PersistenceService } from '../../services/PersistenceService';
import { SyncHealthService } from '../../services/SyncHealthService';
import { debounce } from '../../utils/performance';

// Types
export interface SyncConfig {
  // Storage routing configuration
  routing?: {
    rules?: StorageRoutingRule[];
    defaultTarget?: StorageTarget;
    sizeThreshold?: number; // bytes
    updateFrequencyThreshold?: number; // updates per minute
  };
  
  // Sync behavior configuration
  behavior?: {
    debounceDelay?: number; // ms
    batchSize?: number;
    maxRetries?: number;
    retryDelay?: number; // ms
    enableAutoSync?: boolean;
    syncOnReconnect?: boolean;
  };
  
  // Lifecycle hooks
  hooks?: {
    beforeSync?: (data: any) => any;
    afterSync?: (result: SyncResult) => void;
    onError?: (error: SyncError) => void;
    onConflict?: (conflict: SyncConflict) => any;
  };
  
  // Monitoring
  monitoring?: {
    trackMetrics?: boolean;
    reportInterval?: number; // ms
    metricsCallback?: (metrics: SyncMetrics) => void;
  };
}

export type StorageTarget = 'localStorage' | 'indexedDB' | 'both';

export interface StorageRoutingRule {
  match: (path: string, value: any, metadata: DataMetadata) => boolean;
  target: StorageTarget;
  priority?: number;
}

export interface DataMetadata {
  path: string;
  size: number;
  updateFrequency: number;
  lastUpdated: number;
  updateCount: number;
}

export type SyncStatus = 'idle' | 'pending' | 'syncing' | 'synced' | 'error' | 'conflict';

export interface SyncQueueItem {
  id: string;
  storeName: string;
  path: string;
  data: any;
  target: StorageTarget;
  priority: number;
  timestamp: number;
  retryCount: number;
  status: SyncStatus;
}

export interface SyncResult {
  success: boolean;
  target: StorageTarget;
  duration: number;
  error?: Error;
}

export interface SyncError {
  code: string;
  message: string;
  item: SyncQueueItem;
  timestamp: number;
}

export interface SyncConflict {
  local: any;
  remote: any;
  path: string;
  timestamp: number;
}

export interface SyncMetrics {
  totalSyncs: number;
  successfulSyncs: number;
  failedSyncs: number;
  averageSyncTime: number;
  queueSize: number;
  pendingItems: number;
  lastSyncTime: number;
}

// Default routing rules
const DEFAULT_ROUTING_RULES: StorageRoutingRule[] = [
  // Large data goes to IndexedDB
  {
    match: (_, __, metadata) => metadata.size > 100 * 1024, // 100KB
    target: 'indexedDB',
    priority: 100
  },
  // Frequently updated data stays in localStorage
  {
    match: (_, __, metadata) => metadata.updateFrequency > 10, // 10 updates/min
    target: 'localStorage',
    priority: 90
  },
  // Complex objects go to IndexedDB
  {
    match: (_, value) => {
      return typeof value === 'object' && 
             value !== null && 
             (Array.isArray(value) ? value.length > 100 : Object.keys(value).length > 50);
    },
    target: 'indexedDB',
    priority: 80
  },
  // User preferences and settings stay in localStorage
  {
    match: (path) => path.includes('settings') || path.includes('preferences'),
    target: 'localStorage',
    priority: 70
  },
  // Game state and save data go to IndexedDB
  {
    match: (path) => path.includes('gameState') || path.includes('save'),
    target: 'indexedDB',
    priority: 60
  }
];

// Sync middleware implementation
export interface SyncMiddleware {
  <
    T extends object,
    Mps extends [StoreMutatorIdentifier, unknown][] = [],
    Mcs extends [StoreMutatorIdentifier, unknown][] = []
  >(
    storeInitializer: StateCreator<T, Mps, Mcs, T>
  ): StateCreator<T, Mps, Mcs, T>;
}

export const createSyncMiddleware = (
  storeName: string,
  config: SyncConfig = {}
): SyncMiddleware => {
  // Merge with defaults
  const finalConfig: Required<SyncConfig> = {
    routing: {
      rules: config.routing?.rules || DEFAULT_ROUTING_RULES,
      defaultTarget: config.routing?.defaultTarget || 'localStorage',
      sizeThreshold: config.routing?.sizeThreshold || 100 * 1024, // 100KB
      updateFrequencyThreshold: config.routing?.updateFrequencyThreshold || 10
    },
    behavior: {
      debounceDelay: config.behavior?.debounceDelay || 500,
      batchSize: config.behavior?.batchSize || 10,
      maxRetries: config.behavior?.maxRetries || 3,
      retryDelay: config.behavior?.retryDelay || 1000,
      enableAutoSync: config.behavior?.enableAutoSync ?? true,
      syncOnReconnect: config.behavior?.syncOnReconnect ?? true
    },
    hooks: config.hooks || {},
    monitoring: {
      trackMetrics: config.monitoring?.trackMetrics ?? true,
      reportInterval: config.monitoring?.reportInterval || 60000, // 1 minute
      metricsCallback: config.monitoring?.metricsCallback
    }
  };

  // Initialize services
  const persistenceService = PersistenceService.getInstance();
  const syncHealthService = SyncHealthService.getInstance(persistenceService);
  
  // Sync queue and metadata
  const syncQueue: Map<string, SyncQueueItem> = new Map();
  const dataMetadata: Map<string, DataMetadata> = new Map();
  const updateFrequencyTracking: Map<string, number[]> = new Map();
  
  // Metrics
  const metrics: SyncMetrics = {
    totalSyncs: 0,
    successfulSyncs: 0,
    failedSyncs: 0,
    averageSyncTime: 0,
    queueSize: 0,
    pendingItems: 0,
    lastSyncTime: 0
  };

  // Determine storage target based on routing rules
  const determineStorageTarget = (path: string, value: any): StorageTarget => {
    const metadata = getOrCreateMetadata(path, value);
    
    // Sort rules by priority
    const sortedRules = [...finalConfig.routing.rules].sort((a, b) => 
      (b.priority || 0) - (a.priority || 0)
    );
    
    // Find matching rule
    for (const rule of sortedRules) {
      if (rule.match(path, value, metadata)) {
        return rule.target;
      }
    }
    
    return finalConfig.routing.defaultTarget;
  };

  // Get or create metadata for a data path
  const getOrCreateMetadata = (path: string, value: any): DataMetadata => {
    let metadata = dataMetadata.get(path);
    
    if (!metadata) {
      metadata = {
        path,
        size: estimateSize(value),
        updateFrequency: 0,
        lastUpdated: Date.now(),
        updateCount: 0
      };
      dataMetadata.set(path, metadata);
    } else {
      // Update metadata
      metadata.size = estimateSize(value);
      metadata.updateCount++;
      
      // Track update frequency
      const now = Date.now();
      let updates = updateFrequencyTracking.get(path) || [];
      updates.push(now);
      
      // Keep only updates from last minute
      const oneMinuteAgo = now - 60000;
      updates = updates.filter(time => time > oneMinuteAgo);
      updateFrequencyTracking.set(path, updates);
      
      metadata.updateFrequency = updates.length;
      metadata.lastUpdated = now;
    }
    
    return metadata;
  };

  // Estimate size of data in bytes
  const estimateSize = (obj: any): number => {
    try {
      return new Blob([JSON.stringify(obj)]).size;
    } catch {
      return 0;
    }
  };

  // Add item to sync queue
  const addToQueue = (path: string, data: any, target: StorageTarget, priority: number = 0) => {
    const id = `${storeName}:${path}:${Date.now()}`;
    const item: SyncQueueItem = {
      id,
      storeName,
      path,
      data,
      target,
      priority,
      timestamp: Date.now(),
      retryCount: 0,
      status: 'pending'
    };
    
    syncQueue.set(id, item);
    metrics.queueSize = syncQueue.size;
    metrics.pendingItems = Array.from(syncQueue.values())
      .filter(item => item.status === 'pending').length;
    
    if (finalConfig.behavior.enableAutoSync) {
      scheduleSync();
    }
  };

  // Process sync queue
  const processSyncQueue = async () => {
    const pendingItems = Array.from(syncQueue.values())
      .filter(item => item.status === 'pending')
      .sort((a, b) => b.priority - a.priority)
      .slice(0, finalConfig.behavior.batchSize);
    
    if (pendingItems.length === 0) return;
    
    // Update sync health
    syncHealthService.updateSyncStatus(storeName, 'syncing', pendingItems.length);
    
    const startTime = Date.now();
    const results = await Promise.allSettled(
      pendingItems.map(item => syncItem(item))
    );
    
    // Update metrics
    const duration = Date.now() - startTime;
    metrics.totalSyncs++;
    metrics.lastSyncTime = Date.now();
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value.success) {
        metrics.successfulSyncs++;
        syncQueue.delete(pendingItems[index].id);
      } else {
        metrics.failedSyncs++;
        handleSyncError(pendingItems[index], result.reason);
      }
    });
    
    // Update average sync time
    metrics.averageSyncTime = 
      (metrics.averageSyncTime * (metrics.totalSyncs - 1) + duration) / metrics.totalSyncs;
    
    // Update queue metrics
    metrics.queueSize = syncQueue.size;
    metrics.pendingItems = Array.from(syncQueue.values())
      .filter(item => item.status === 'pending').length;
    
    // Update sync health
    syncHealthService.updateSyncStatus(
      storeName, 
      metrics.pendingItems > 0 ? 'pending' : 'synced',
      0
    );
    
    // Report metrics
    if (finalConfig.monitoring.trackMetrics && finalConfig.monitoring.metricsCallback) {
      finalConfig.monitoring.metricsCallback(metrics);
    }
    
    // Process more items if needed
    if (metrics.pendingItems > 0) {
      scheduleSync();
    }
  };

  // Sync individual item
  const syncItem = async (item: SyncQueueItem): Promise<SyncResult> => {
    const startTime = Date.now();
    
    try {
      item.status = 'syncing';
      
      // Skip if data is null/undefined
      if (item.data === null || item.data === undefined) {
        console.debug(`[SyncMiddleware] Skipping sync for ${item.path} - data is null/undefined`);
        item.status = 'synced';
        return {
          success: true,
          target: item.target,
          duration: Date.now() - startTime
        };
      }
      
      // Apply before sync hook
      let dataToSync = item.data;
      if (finalConfig.hooks.beforeSync) {
        try {
          dataToSync = finalConfig.hooks.beforeSync(dataToSync);
        } catch (hookError) {
          console.error(`[SyncMiddleware] beforeSync hook error for ${item.path}:`, hookError);
          // Continue with original data if hook fails
          dataToSync = item.data;
        }
      }
      
      // Perform sync based on target
      if (item.target === 'localStorage') {
        await persistenceService.syncToLocalStorage(`${storeName}:${item.path}`, dataToSync);
      } else if (item.target === 'indexedDB') {
        await persistenceService.syncToIndexedDB(storeName, dataToSync);
      } else if (item.target === 'both') {
        await Promise.all([
          persistenceService.syncToLocalStorage(`${storeName}:${item.path}`, dataToSync),
          persistenceService.syncToIndexedDB(storeName, dataToSync)
        ]);
      }
      
      item.status = 'synced';
      const result: SyncResult = {
        success: true,
        target: item.target,
        duration: Date.now() - startTime
      };
      
      // Apply after sync hook
      if (finalConfig.hooks.afterSync) {
        finalConfig.hooks.afterSync(result);
      }
      
      return result;
    } catch (error) {
      item.status = 'error';
      const result: SyncResult = {
        success: false,
        target: item.target,
        duration: Date.now() - startTime,
        error: error as Error
      };
      
      throw error;
    }
  };

  // Handle sync errors
  const handleSyncError = (item: SyncQueueItem, error: any) => {
    item.retryCount++;
    
    if (item.retryCount < finalConfig.behavior.maxRetries) {
      // Schedule retry
      item.status = 'pending';
      setTimeout(() => {
        if (finalConfig.behavior.enableAutoSync) {
          scheduleSync();
        }
      }, finalConfig.behavior.retryDelay * item.retryCount);
    } else {
      // Max retries reached
      item.status = 'error';
      const syncError: SyncError = {
        code: 'SYNC_FAILED',
        message: error?.message || 'Unknown error',
        item,
        timestamp: Date.now()
      };
      
      if (finalConfig.hooks.onError) {
        finalConfig.hooks.onError(syncError);
      }
      
      // Report to sync health service
      syncHealthService.recordSyncError(storeName, syncError.message);
    }
  };

  // Debounced sync scheduling
  const scheduleSync = debounce(
    processSyncQueue,
    finalConfig.behavior.debounceDelay
  );
  
  // Function to cancel any pending scheduled syncs
  const cancelScheduledSync = () => {
    scheduleSync.cancel?.();
  };

  // Handle online/offline events
  if (finalConfig.behavior.syncOnReconnect) {
    window.addEventListener('online', () => {
      if (metrics.pendingItems > 0) {
        scheduleSync();
      }
    });
  }

  // Periodic metrics reporting
  if (finalConfig.monitoring.trackMetrics && finalConfig.monitoring.reportInterval > 0) {
    setInterval(() => {
      if (finalConfig.monitoring.metricsCallback) {
        finalConfig.monitoring.metricsCallback(metrics);
      }
    }, finalConfig.monitoring.reportInterval);
  }

  // Return middleware
  return (storeInitializer) => (set, get, api) => {
    // Wrap set function to intercept updates
    const wrappedSet: typeof set = (partial, replace) => {
      // Get current state
      const currentState = get();
      
      // Apply update
      set(partial, replace);
      
      // Get new state
      const newState = get();
      
      // Find changed paths
      const changes = findChangedPaths(currentState, newState);
      
      // Queue sync for each changed path
      changes.forEach(({ path, value }) => {
        // const timestamp = new Date().toISOString();
        
        // Log cash-related changes
        /*if (path.includes('cash') || path.includes('finances')) {
          console.log(`[${timestamp}] [SyncMiddleware.${storeName}] Change detected:`, {
            path,
            value: path.includes('cash') ? value : '[finances object]',
            valueType: typeof value
          });
        }*/
        
        // Skip null/undefined values or if we're in a reset state
        if (value === null || value === undefined) {
          // console.debug(`[${timestamp}] [SyncMiddleware.${storeName}] Skipping sync for ${path} - value is null/undefined`);
          return;
        }
        
        // Check if parent path exists (e.g., for "finances.dailyStats.salesBreakdowns", check if "finances" and "finances.dailyStats" exist)
        if (path.includes('.')) {
          const pathParts = path.split('.');
          let currentObj = newState;
          
          for (let i = 0; i < pathParts.length - 1; i++) {
            currentObj = currentObj?.[pathParts[i]];
            if (currentObj === null || currentObj === undefined) {
              console.debug(`[SyncMiddleware] Skipping sync for ${path} - parent object "${pathParts.slice(0, i + 1).join('.')}" is null/undefined`);
              return;
            }
          }
        }
        
        const target = determineStorageTarget(path, value);
        const metadata = getOrCreateMetadata(path, value);
        
        // Determine priority based on metadata
        let priority = 0;
        if (metadata.size > finalConfig.routing.sizeThreshold) priority += 10;
        if (metadata.updateFrequency > finalConfig.routing.updateFrequencyThreshold) priority += 5;
        
        addToQueue(path, value, target, priority);
      });
    };
    
    // Initialize store with wrapped set
    const store = storeInitializer(wrappedSet, get, api);
    
    // Add sync control methods
    const syncControls = {
      syncNow: () => processSyncQueue(),
      getSyncStatus: () => ({
        metrics,
        queueSize: syncQueue.size,
        pendingItems: Array.from(syncQueue.values()).filter(item => item.status === 'pending')
      }),
      clearSyncQueue: () => {
        cancelScheduledSync();
        syncQueue.clear();
        metrics.queueSize = 0;
        metrics.pendingItems = 0;
      },
      pauseSync: () => {
        finalConfig.behavior.enableAutoSync = false;
        cancelScheduledSync();
      },
      resumeSync: () => {
        finalConfig.behavior.enableAutoSync = true;
        if (metrics.pendingItems > 0) {
          scheduleSync();
        }
      }
    };
    
    // Merge sync controls with store
    return Object.assign(store, { sync: syncControls });
  };
};

// Helper to find changed paths between states
function findChangedPaths(
  oldState: any,
  newState: any,
  basePath: string = ''
): Array<{ path: string; value: any }> {
  const changes: Array<{ path: string; value: any }> = [];
  
  // Handle primitive values
  if (oldState === newState) {
    return changes;
  }
  
  if (
    typeof oldState !== 'object' ||
    typeof newState !== 'object' ||
    oldState === null ||
    newState === null
  ) {
    changes.push({ path: basePath || 'root', value: newState });
    return changes;
  }
  
  // Handle arrays
  if (Array.isArray(oldState) || Array.isArray(newState)) {
    if (JSON.stringify(oldState) !== JSON.stringify(newState)) {
      changes.push({ path: basePath || 'root', value: newState });
    }
    return changes;
  }
  
  // Handle objects
  const allKeys = new Set([...Object.keys(oldState), ...Object.keys(newState)]);
  
  for (const key of allKeys) {
    const oldValue = oldState[key];
    const newValue = newState[key];
    const currentPath = basePath ? `${basePath}.${key}` : key;
    
    if (oldValue !== newValue) {
      if (
        typeof oldValue === 'object' &&
        typeof newValue === 'object' &&
        oldValue !== null &&
        newValue !== null &&
        !Array.isArray(oldValue) &&
        !Array.isArray(newValue)
      ) {
        // Recursively check nested objects
        changes.push(...findChangedPaths(oldValue, newValue, currentPath));
      } else {
        changes.push({ path: currentPath, value: newValue });
      }
    }
  }
  
  return changes;
}

// Type augmentation for stores with sync controls
export interface WithSyncControls {
  sync: {
    syncNow: () => Promise<void>;
    getSyncStatus: () => {
      metrics: SyncMetrics;
      queueSize: number;
      pendingItems: SyncQueueItem[];
    };
    clearSyncQueue: () => void;
    pauseSync: () => void;
    resumeSync: () => void;
  };
}